import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, Input, Typography } from '@mui/joy';
import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { CrewService } from '../services/CrewService';
import { SharedSession } from '../services/types/api.generated';
import campaignStore from '../stores/CampaignStore';
import sessionStore from '../stores/SessionStore';
import QRModal from './Crew/QRModal';
import DynamicTable, { TableColumn } from './DynamicTable';
import TableHeader from './TableHeader';

type SharedSessionRow = SharedSession & { id: string };

const SharedSessionList = observer(() => {
  const { loading, error } = campaignStore;
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedSessions, setSharedSessions] = useState<SharedSessionRow[]>([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrModalLink, setQrModalLink] = useState('');
  const navigate = useNavigate();
  const organizationId = sessionStore.user?.last_selected_org;

  const joinSharedSession = async (sessionId: string, deviceUuid: string): Promise<void> => {
    navigate(`/crew/${sessionId}/device/${deviceUuid}`);
  };

  const handleCopyLink = (event: React.MouseEvent, sessionId: string, deviceUuid: string): void => {
    event.stopPropagation();
    const link = `${window.location.origin}/crew/${sessionId}/device/${deviceUuid}`;
    navigator.clipboard.writeText(link);
    toast.success(t('crew.link_copied'));
  };

  const handleShowQRCode = (event: React.MouseEvent, sessionId: string, deviceUuid: string): void => {
    event.stopPropagation();
    const link = `${window.location.origin}/crew/${sessionId}/device/${deviceUuid}`;
    setQrModalLink(link);
    setQrModalOpen(true);
  };

  const filteredSharedSessions = useMemo(
    () =>
      sharedSessions.filter(
        (session) =>
          !searchQuery ||
          session.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          session.device_uuid?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [sharedSessions, searchQuery]
  );

  useEffect(() => {
    const fetchSharedSessions = async () => {
      if (!organizationId) {
        return;
      }
      const response = await CrewService.listByOrganization(organizationId?.toString() || '');
      if (response) {
        const validSessions = response
          .filter((session) => !!session.id)
          .map((session) => ({ ...session, id: session.id! }));
        setSharedSessions(validSessions);
      }
    };
    fetchSharedSessions();
  }, [organizationId]);

  const columns: Array<TableColumn<SharedSessionRow, keyof SharedSessionRow>> = [
    {
      header: t('crew.table.session_name'),
      key: 'id',
      width: '30%',
      render: (value) => <Typography level='body-md'>{value || t('dashboard.n_a')}</Typography>
    },
    {
      header: t('crew.table.aircraft_name'),
      key: 'device_name',
      width: '25%',
      render: (v, row) => <Typography level='body-md'>{row.device_name || t('dashboard.n_a')}</Typography>
    },
    {
      header: t('crew.table.copy_link'),
      key: 'id',
      width: '30%',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size='sm'
            variant='plain'
            color='neutral'
            onClick={(e) => handleCopyLink(e, row.id, row.device_uuid || '')}
            title={t('crew.table.copy_link')}
          >
            <ContentCopyIcon />
          </IconButton>
        </Box>
      )
    },
    {
      header: t('crew.table.qr_code'),
      key: 'id',
      width: '15%',
      render: (value, row) => (
        <Box>
          <IconButton
            size='sm'
            variant='outlined'
            color='neutral'
            onClick={(e) => handleShowQRCode(e, row.id, row.device_uuid || '')}
            title={t('crew.table.show_qr_code')}
          >
            <QrCodeIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <>
      <TableHeader title={t('crew.table.header')}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: 2 }}>
          <Input
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('crew.search_placeholder')}
            startDecorator={<SearchIcon />}
            sx={{
              '--Input-minHeight': '2.5rem',
              maxWidth: '25rem',
              width: '100%'
            }}
            value={searchQuery}
          />
        </Box>
      </TableHeader>

      <Box sx={{ padding: '20px' }}>
        {loading && (
          <Box sx={{ textAlign: 'center', padding: '50px' }}>
            <CircularProgress />
            <Typography level='body-md' marginTop='15px'>
              {t('loading')}
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ textAlign: 'center', padding: '50px' }}>
            <Typography color='danger' level='body-md'>
              {t('crew.loading_failed')}
            </Typography>
          </Box>
        )}
        {!loading && !error && (
          <>
            {filteredSharedSessions.length > 0 ? (
              <DynamicTable
                columns={columns}
                data={filteredSharedSessions}
                selectedRows={[]}
                checkbox={false}
                onRowClick={(row) => joinSharedSession(row.id || '', row.device_uuid || '')}
              />
            ) : (
              <Box sx={{ textAlign: 'center', padding: '50px' }}>
                <Typography level='body-md'>{t('crew.no_crew_found')}</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <QRModal linkToShare={qrModalLink} isOpen={qrModalOpen} onClose={() => setQrModalOpen(false)} />
    </>
  );
});

export default SharedSessionList;
